import './landing-page-content-loader';
import { html, css } from 'lit';
import { property } from 'lit/decorators.js';
import { KryptonElement } from '../krypton-element';
import { getNameSpace } from '../../modules/global_function';

// All the widgets in the landing page has some common properties and behavior.  They are implemented in this base widget
export class BaseWidget extends KryptonElement {
    static get styles() {
        return css`
          .widgetbox {
                border: 1px solid #75787b;
                border-radius: 5px;
                background: #ffffff;
                margin-right: 15px;
                margin-bottom: 15px;
                margin-top: 10px;
                min-height: 40px;
            }

            .widgetbox-body {
                padding: 5px;
            }

            .widgetbox-body a {
                font-weight: normal;
            }

            .circle-icon {
                position: absolute;
                bottom: 2px;
                right: 2px;
                padding: 0 0 0 46px;
                padding-top: 3px;
                float: right;
                margin-right: 5px;
                margin-bottom: 5px;
                border: 1px solid rgba(255, 255, 255, 0.0);
                background: url("/resources/images/icons/Grey Circle Outline.svg") no-repeat bottom right;
            }

            .circle-check-icon {
                padding: 0 0 0 46px;
                padding-top: 3px;
                float: right;
                margin-right: 5px;
                margin-bottom: 5px;
                border: 1px solid rgba(255, 255, 255, 0.0);
                background: url("/resources/images/icons/nominations_confirmations.svg") no-repeat bottom right;
            }

            .overlay-boxes {
                min-width: 120px;
                min-height: 70px;
                height: 100px;
                width: 120px;
                font-size: 16px;
                color: #212121;
                background-color: #ededed;
                border: 1px solid #cccccc;
                border-radius: 5px;
                float: left;
                text-align: center;
                vertical-align: middle;
                margin: 2px;
                cursor: pointer;
                position: relative;
            }
        `;
    }

    private _widgetId: string = '';
    private _widgetTitle: string = '';
    private _state: string = '';
    private _iconified: boolean = false;
    private _isIconSelected: boolean = false;

    // Begin getters and setters for properties
    get widgetId(): string {
        return this._widgetId;
    }
    @property({ type: String })
    set widgetId(value: string) {
        const oldValue = this._widgetId;
        this._widgetId = value;
        this.requestUpdate('widgetId', oldValue);
    }

    get widgetTitle(): string {
        return this._widgetTitle;
    }

    @property({ type: String })
    set widgetTitle(value: string) {
        const oldValue = this._widgetTitle;
        this._widgetTitle = value;
        this.requestUpdate('widgetTitle', oldValue);
    }

    get state(): string {
        return this._state;
    }

    @property({ type: String })
    set state(value: string) {
        const oldValue = this._state;
        this._state = value;
        this.requestUpdate('state', oldValue);
        this.showOrHideForState();
    }

    get iconified(): boolean {
        return this._iconified;
    }

    @property({ type: Boolean })
    set iconified(value: boolean) {
        const oldValue = this._iconified;
        this._iconified = value;
        this.requestUpdate('iconified', oldValue);
        this.processChangeToIconifiedState(this._iconified);
    }

    get isIconSelected(): boolean {
        return this._isIconSelected;
    }

    @property({ type: Boolean })
    set isIconSelected(value: boolean) {
        const oldValue = this._isIconSelected;
        this._isIconSelected = value;
        this.requestUpdate('isIconSelected', oldValue);
    }
    // End getters and setters for properties

    // Place holder - If there is a refresh interval in workspace-widgets, overriding this method shoulod be useful
    processChangeToIconifiedState(isIcon: boolean) { }
    // When the widget is minimized, the content has to be hidden and min icon should be changed to max icon.
    // Similarly when it is maximized, do the other way round
    showOrHideForState() {
        let content = this.querySelector('.widget-content-class') || this.shadowRoot?.querySelector('.widget-content-class') as any;
        var minMax = this.querySelector('.min-window-icon') || this.shadowRoot?.querySelector('.min-window-icon') as any;
        if (!content) return;
        let isMin = this._state === 'M';
        let curMinimized = content.style.display === "none";
        if (isMin != curMinimized) {
            try {
                getNameSpace('landingPage', 'mainPage').slideToggle(content, isMin);
            } catch (e) {
                if (isMin) {
                    content.style && (content.style.display = "none");
                }
            }
        }
        if (minMax) {
            if (isMin) {
                minMax.classList.remove('min');
                minMax.classList.add('max');
            } else {
                minMax.classList.remove('max');
                minMax.classList.add('min');
            }
        }
    }

    // Handler for min/max icon click - change the state and call the landing page update for committing the state to the database
    minimizeOrMaximizeWidget(e: Event) {
        e.stopPropagation();
        e.preventDefault();
        if (this._state === 'M') {
            this.state = '';
        } else {
            this.state = 'M';
        }
        getNameSpace('landingPage', 'mainPage').updateSortOrder();
        return false;
    }

    // Handler for close icon click - Remove the widget and call the landing page update for committing the state to the database
    closeWidget(e: Event) {
        e.stopPropagation();
        e.preventDefault();
        this.remove();
        getNameSpace('landingPage', 'mainPage').updateSortOrder();
    }

    toggleCheckBoxSelection(e: Event) {
        //Check/uncheck the boxes
        var el = e.target as any;
        el = el.parentNode.parentNode.querySelector('.circle-icon');
        if (el) {
            if (this.isIconSelected) {
                el.classList.remove('circle-check-icon');
            } else {
                el.classList.add('circle-check-icon');
            }
            this.isIconSelected = !this.isIconSelected;
        }
    }

    // This renders the UI for the widget.  This takes care of the common layout and there are some methods that are just place holders.
    // Derived widgets should take care of overriding these methods if it needs a different behavior.
    // For example, in workspace and info widgets, getContentLoaderSlot() method should have a <landing-page-content-loader>, where as quick link should have a <slot>
    render() {
        return html`
            ${this.iconified ? html`${this.renderIconifiedWidget()}` : html`${this.renderNormalWidget()}`}
        `;
    }

    renderNormalWidget() {
        return html`
            <div class="widgetbox">
                <slot name="widgethandle" @slotchange="${this.onSlotChange}"></slot>
                ${this.getHandle()}
                <!-- The below method will be overridden in derived widgets -->
                <div class="widgetbox-body">${this.getContentLoaderSlot()}</div>
            </div>
        `;
    }

    renderIconifiedWidget() {
        return html`
            <div class="widget-box">
                <div class="overlay-boxes" @click="${(e: Event) => this.toggleCheckBoxSelection(e)}">
                <div class="position-relative">
                    <p class="widget-p-postion">${this.widgetTitle}</p>
                </div>
                <div class="position-relative">
                    <div class="circle-icon" id="wdgt_${this.widgetId}">&nbsp;</div>
                </div>
                </div>
            </div>
        `;
    }

    // jQuery UI needs the handle to be outside shadow DOM, but the properties and behavior inside the handle elements are rendered in the shadow DOM
    // To render the handle inside a div that jQuery UI can access, we do the following steps
    // 1. Created a slot with name "widgethandle"
    // 2. Every widget will provide a slot - a div which will be the handle to jQuery UI
    // 3. The widget render will render the header contents to a div with id widgetbox-header
    // 4. After the rendering, when the slot gets changed, move the contents in widgetbox-header to the div from the slot
    onSlotChange(e: Event) {
        var el = e.target as any;
        const childNodes = el.assignedNodes({ flatten: true });
        if (childNodes && childNodes.length) {
            childNodes[0].childNodes.forEach(element => { element.remove(); });
            childNodes[0].appendChild(this.shadowRoot?.querySelector('#widgetbox-header'));
        }
    }

    // The title bar which is the handle for jQuery UI sortable API
    getHandle() {
        return html` <div id="widgetbox-header">
      <p class="float-left widget-header-title">${this.getWidgetTitle()}</p>

      <!-- Just for debugging, this displays the widget id.  Can be overridden if there is a need to display more info -->
      ${this.getWidgetIdForDebug()}

      <a href="#" class="close-icon float-right hidden-md-down" @click="${(e: Event) => this.closeWidget(e)}">&nbsp;</a>
      <a href="#" class="min min-window-icon float-right hidden-md-down" @click="${(e: Event) => this.minimizeOrMaximizeWidget(e)}" >&nbsp;</a>
      <div class="clearfix"></div>
    </div>`;
    }

    // Place holder for the title of the widget.  Can be overridden by derived widgets (example - WS widget may have a href)
    getWidgetTitle() {
        return html`${this.widgetTitle}`;
    }

    // Place holder for debug info.  Can be overridden by derived widgets if there is a need
    getWidgetIdForDebug() {
        return html`<span class="absolute_hidden">${this._widgetId}</span>`;
    }

    // Place holder for the content of the widget.  Should be overridden by derived widgets
    getContentLoaderSlot() {
        return html`&nbsp;`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'base-widget': BaseWidget;
    }
}
